import { CartEntrySummary } from "./CartEntrySummary";
import { CartEntryDetails } from "./CartEntryDetails";
import { Card } from "~/common/mui";
import { CheckoutProductLine } from "./CheckoutProductLine";
import { CartProductLine } from "./CartProductLine";
import { memo, useCallback, useEffect, useRef } from "react";
import type { CartEntryRowViewModel, ProductLineViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import { useLineItemActions } from "~/domains/cart/hooks";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

interface CartEntryProps {
  entry: CartEntryRowViewModel;
  editMode: boolean;
  onRender: (rowId: string) => void;
  onDeleteProduct: (cartEntryId: number) => void;
  onDeleteLine: (args: { cartEntryId: number; lineItemId: number }) => void;
  onCopyLine: (packEntryId: number) => void;
}

interface EditableLineWrapperProps {
  line: ProductLineViewModel;
  mainDescriptor: ProductDescriptor;
  onDeleteLine: (line: ProductLineViewModel) => void;
}

const EditableLineWrapper = ({ line, mainDescriptor, onDeleteLine }: EditableLineWrapperProps) => {
  const product = line.descriptor ?? mainDescriptor;
  const actions = useLineItemActions({ lineItem: line.sourceLineItem, product });
  return <CartProductLine line={line} product={product} actions={actions} onDeleteLine={() => onDeleteLine(line)} />;
};

export const CartEntry = memo(
  ({ entry, editMode, onRender, onDeleteProduct, onDeleteLine, onCopyLine }: CartEntryProps) => {
    const mainDescriptor = entry.mainDescriptor;

    const rootRef = useRef<HTMLDivElement>(null);
    const onRenderRef = useRef(onRender);
    onRenderRef.current = onRender;
    const rowIdRef = useRef(entry.rowId);
    rowIdRef.current = entry.rowId;

    useEffect(() => {
      const element = rootRef.current;
      if (!element) {
        return;
      }

      const resizeObserver = new ResizeObserver(() => {
        onRenderRef.current(rowIdRef.current);
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    const handleDeleteProduct = useCallback(() => {
      onDeleteProduct(entry.cartEntryId);
    }, [onDeleteProduct, entry.cartEntryId]);

    const handleDeleteLine = useCallback(
      (line: ProductLineViewModel) => {
        onDeleteLine({ cartEntryId: entry.cartEntryId, lineItemId: line.lineItemId });
      },
      [onDeleteLine, entry.cartEntryId],
    );

    const renderItem = useCallback(
      (line: ProductLineViewModel) => {
        const product = line.descriptor ?? mainDescriptor;
        if (editMode) {
          return (
            <EditableLineWrapper
              key={line.rowId}
              line={line}
              mainDescriptor={mainDescriptor!}
              onDeleteLine={handleDeleteLine}
            />
          );
        }
        return <CheckoutProductLine key={line.rowId} line={line} product={product!} />;
      },
      [editMode, mainDescriptor, handleDeleteLine],
    );

    if (!mainDescriptor) {
      return null;
    }

    return (
      <Card ref={rootRef}>
        <CartEntrySummary
          product={mainDescriptor}
          totalQuantity={entry.summary.totalQuantity}
          lineItemCount={entry.summary.lineItemCount}
          subtotal={entry.summary.subtotal}
          hasTariffSurcharge={entry.summary.hasTariffSurcharge}
          hasIncludedSurcharge={entry.summary.hasIncludedSurcharge}
          canDeleteProduct={entry.summary.canDeleteProduct}
          editMode={editMode}
          onDeleteProduct={handleDeleteProduct}
        />
        <CartEntryDetails
          canCopyLine={entry.details.canCopyLine}
          copySourcePackEntryId={entry.details.copySourcePackEntryId}
          lines={entry.details.lines}
          intraEntryChainInsertAfterLineIds={entry.details.intraEntryChainInsertAfterLineIds}
          renderItem={renderItem}
          editMode={editMode}
          initiallyExpanded={entry.details.initiallyExpanded}
          onCopyLine={onCopyLine}
        />
      </Card>
    );
  },
);

CartEntry.displayName = "CartEntry";
